import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePatientSearchForQuickStart, type PatientSearchOption } from "@/hooks/usePatientSearchForQuickStart";
import { sortIncidentPetOptions, type IncidentPetOption } from "./stayIncidentModel";

type PresentReservationRow = {
  pet_id: string;
  pets: { name: string | null } | null;
};

export function useIncidentPetOptions({
  day,
  primaryPetId,
  search,
}: {
  day: string;
  primaryPetId: string;
  search: string;
}) {
  const patientSearch = usePatientSearchForQuickStart(search);
  const presentPets = useQuery({
    queryKey: ["incident-present-pets", day],
    enabled: Boolean(day),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stay_reservations")
        .select("pet_id, pets ( name )")
        .lte("check_in_date", day)
        .or(`check_out_date.is.null,check_out_date.gte.${day}`);
      if (error) throw error;
      return (data as unknown as PresentReservationRow[] | null ?? [])
        .filter((row) => row.pet_id !== primaryPetId)
        .map((row) => ({ petId: row.pet_id, petName: row.pets?.name?.trim() || row.pet_id }));
    },
  });

  return useQuery({
    queryKey: ["incident-pet-options", day, primaryPetId, search],
    enabled: patientSearch.isSuccess || presentPets.isSuccess,
    queryFn: async (): Promise<IncidentPetOption[]> => {
      const present = presentPets.data ?? [];
      const presentPetIds = new Set(present.map((pet) => pet.petId));
      const byId = new Map<string, IncidentPetOption>();

      for (const pet of present) byId.set(pet.petId, { ...pet, isPresent: true });
      for (const option of (patientSearch.data ?? []) as PatientSearchOption[]) {
        if (option.petId !== primaryPetId) {
          byId.set(option.petId, {
            petId: option.petId,
            petName: option.petName,
            ownerName: option.ownerName,
            photoPath: option.petPhotoPath,
            isPresent: presentPetIds.has(option.petId),
          });
        }
      }

      return sortIncidentPetOptions([...byId.values()], presentPetIds);
    },
  });
}
